// @flow

import {useArticle, usePrefs} from 'docs/data';
import type {ArticlePage} from 'docs/data';

// Articles can contain multiple "pages" which can be toggled via prefs. This
// is most commonly used to toggle between platforms (eg. Web, iOS, Android).
// Typically, page prefs are toggled using the PageSelector component which
// allows switching between a single pref, but custom page selectors can also
// be implemented for more complex scenarios.
export const useActivePage = (): ArticlePage => {
  const {article} = useArticle();
  const {getPref} = usePrefs();

  // Filter pages which don't match the user's current values for the article's
  // page prefs. If the user doesn't have a setting for a given pref, or if
  // none of the pages match the user's value for the pref, ignore it.
  //
  // The active page is then: either the last page remaining (indicating we had an
  // exact match), or if multiple pages remain, the first of the remaining pages.
  const matchingPages = article.pagePrefs.reduce((remainingPages, pref) => {
    const value = getPref(pref);

    // If we've already found an answer, or the user doesn't have a value for
    // the given pref, there's nothing for us to filter.
    if (!value || remainingPages.length === 1) {
      return remainingPages;
    }

    // Find all pages which match the user's value for the pref.
    const filteredPages = remainingPages.filter(
      (page) => page.prefs[pref] === value,
    );

    // If none of the pages match the user's value for the pref, ignore it.
    // Otherwise, remove all pages which don't match the pref and continue
    // with the next pref.
    return filteredPages.length === 0 ? remainingPages : filteredPages;
  }, article.pages);

  return matchingPages[0];
};
